import { useCallback, useEffect, useState } from "react";
import { authenticatedApi, publicApi } from "./api";
import { isLogged } from "../Stores/AuthStore";


type Options = {
  params?: any;
  enabled?: boolean;
}
type OptionsList = {
  total?: number;
}

type ReturnType<TData> = {
  data: TData | undefined;
  error: Error | undefined;
  isInitialLoading: boolean;
  isLoading: boolean;
  refetch: () => Promise<TData | null>;
}

interface IListResult<T> {
  nodes: T[];
  pageInfos: {
    hasNextPage: boolean;
    hasPreviousPage: boolean;
    nextPage: number;
    previousPage: number;
    totalCount: number;
    totalPages: number;
  };
}

export function useApiListQuery<TData>(relativeUrl: string, options?: Options & OptionsList): Omit<ReturnType<IListResult<TData>>, 'data'> & {
  list: IListResult<TData>;
  fetchMore: () => Promise<IListResult<TData> | null>;
} {

  const { params } = options || {};
  const serializedParams = JSON.stringify(params);

  const [data, setData] = useState<IListResult<TData> | undefined>();
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | undefined>();
  const userIsLogged = isLogged();

  const fetchData = async (): Promise<IListResult<TData> | null> => {
    setIsLoading(true);
    const api = userIsLogged ? authenticatedApi : publicApi;
    try {
      const response = await api.get(relativeUrl, {
        params: {
          ...params,
          total: options?.total || 100,
        }
      });
      setData(response.data as IListResult<TData>);
      return response.data as IListResult<TData>;
    } catch (error: Error | any) {
      setError(error);
    } finally {
      setIsInitialLoading(false);
      setIsLoading(false);
    }
    return null;
  };

  const fetchMore = async (): Promise<IListResult<TData> | null> => {
    if (!data?.pageInfos.hasNextPage) {
      return null;
    }
    setIsLoading(true);
    const api = userIsLogged ? authenticatedApi : publicApi;
    try {
      const response = await api.get(relativeUrl, {
        params: {
          ...params,
          page: data?.pageInfos.nextPage,
          total: options?.total || 10,
        },
      });

      // merge data
      const newData = {
        ...response.data,
        nodes: [
          ...(data?.nodes || []),
          ...response.data.nodes,
        ],
      } as IListResult<TData>;

      setData(newData);
      return newData;
    } catch (error: Error | any) {
      setError(error);
    } finally {
      setIsInitialLoading(false);
      setIsLoading(false);
    }
    return null;
  };

  useEffect(() => {
    if (options?.enabled === false) {
      return;
    }
    // reset all data when params change
    setData(undefined);
    setIsInitialLoading(true);
    setIsLoading(true);
    setError(undefined);
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serializedParams, options?.enabled]);

  return {
    list: data as IListResult<TData>,
    error,
    isInitialLoading,
    isLoading,
    refetch: fetchData,
    fetchMore,
  };
}

export default function useApiQuery<TData>(relativeUrl: string, options?: Options): ReturnType<TData> {

  const { params } = options || {};
  const serializedParams = JSON.stringify(params);

  const [data, setData] = useState<TData | undefined>();
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | undefined>();
  const userIsLogged = isLogged();

  const fetchData = useCallback(async (): Promise<TData | null> => {
    setIsLoading(true);
    const api = userIsLogged ? authenticatedApi : publicApi;
    try {
      const response = await api.get(relativeUrl, {
        params,
      });
      setData(response.data as TData);
      return response.data as TData;
    } catch (error: Error | any) {
      setError(error);
    } finally {
      setIsInitialLoading(false);
      setIsLoading(false);
    }
    return null;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [relativeUrl, userIsLogged, serializedParams]);

  useEffect(() => {
    if (options?.enabled === false) {
      setData(undefined);
      setIsInitialLoading(false);
      setIsLoading(false);
      setError(undefined);
      return;
    }
    fetchData();
  }, [fetchData, options?.enabled]);

  return {
    data,
    error,
    isInitialLoading,
    isLoading,
    refetch: fetchData,
  };
}